import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", company: "", machine: "", message: "" };

export default function Contact({ products, enquiryMachine }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (enquiryMachine) setForm((f) => ({ ...f, machine: enquiryMachine }));
  }, [enquiryMachine]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const isPreview = window.location.hostname.includes("preview") || window.location.hostname === "localhost";
    const url = isPreview ? `${API}/enquiries` : "api/enquiry.php";
    try {
      await axios.post(url, form);
      toast.success("Enquiry sent. Our team will be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your enquiry. Please call us on +61 404 040 920.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "rounded-none border-input bg-white text-[#333] placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-navy";

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#F4F5F7]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-16 sm:px-8 lg:grid-cols-5 lg:gap-16 lg:py-20">
        <div className="lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-laser">Get in Touch</p>
          <h2 className="mt-3 text-3xl font-bold text-[#222] sm:text-4xl" data-testid="contact-heading">
            Request a Quote
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            Tell us about your material, thickness and production targets - we will recommend the right machine and power level for the job.
          </p>
          <div className="mt-8 space-y-0 border-t border-border">
            <a href="tel:+61404040920" data-testid="contact-phone" className="flex items-center gap-4 border-b border-border py-5 transition-colors duration-150 hover:text-laser">
              <Phone size={24} weight="duotone" className="shrink-0 text-laser" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Call Us</div>
                <div className="mt-1 text-lg font-semibold text-[#222]">+61 404 040 920</div>
              </div>
            </a>
            <a href="mailto:sales@pentalaser.com.au" data-testid="contact-email" className="flex items-center gap-4 border-b border-border py-5 transition-colors duration-150 hover:text-laser">
              <EnvelopeSimple size={24} weight="duotone" className="shrink-0 text-laser" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Email</div>
                <div className="mt-1 text-lg font-semibold text-[#222]">sales@pentalaser.com.au</div>
              </div>
            </a>
            <a
              href="https://www.google.com/maps/place/Penta+Laser/@-38.012819,145.269994,16z"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address"
              className="flex items-center gap-4 border-b border-border py-5 transition-colors duration-150 hover:text-laser"
            >
              <MapPin size={24} weight="duotone" className="shrink-0 text-laser" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Office</div>
                <div className="mt-1 text-lg font-semibold text-[#222]">18-20 Star Crescent, Hallam VIC 3803</div>
              </div>
            </a>
          </div>
        </div>
        <form onSubmit={submit} data-testid="enquiry-form" className="border border-border bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)] sm:p-10 lg:col-span-3">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="enquiry-name" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Name *</label>
              <Input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
            </div>
            <div>
              <label htmlFor="enquiry-email" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Email *</label>
              <Input id="enquiry-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com.au" />
            </div>
            <div>
              <label htmlFor="enquiry-phone" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Phone</label>
              <Input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="04xx xxx xxx" />
            </div>
            <div>
              <label htmlFor="enquiry-company" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Company</label>
              <Input id="enquiry-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Machine of Interest</label>
              <Select value={form.machine} onValueChange={(v) => setForm((f) => ({ ...f, machine: v }))}>
                <SelectTrigger data-testid="enquiry-machine-select" className="rounded-none border-input bg-white focus:ring-2 focus:ring-navy">
                  <SelectValue placeholder="Select a machine (optional)" />
                </SelectTrigger>
                <SelectContent className="rounded-none border-border bg-white">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.name} data-testid={`enquiry-machine-option-${p.id}`}>
                      {p.name} - {p.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-message" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Message *</label>
              <Textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="Material, thickness, sheet size, production volume..." />
            </div>
          </div>
          <button
            type="submit"
            data-testid="enquiry-submit-btn"
            disabled={sending}
            className="mt-8 w-full bg-laser px-7 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-150 hover:bg-laser-hover disabled:opacity-50"
          >
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </form>
      </div>
    </section>
  );
}
